/** Wait for rebalancing background tasks — WebSocket updates plus HTTP poll until terminal. */

import axios from "axios";
import type { SnapshotTaskStatus } from "@/services/api/data-hub/types";
import { getTaskStatus } from "@/services/api/tasks";
import { taskWebSocket } from "@/services/taskWebSocket";

export interface RebalancingTaskResponse {
  task_id: string;
  status: string;
}

const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled", "partial_failure"]);
const FALLBACK_POLL_INTERVAL_MS = 3000;

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function _extractErrorDetail(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => String(d)).join(", ");
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

function _isResultNotReady(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  if (err.response?.status !== 400) return false;
  const detail = _extractErrorDetail(err).toLowerCase();
  return detail.includes("not ready") || detail.includes("status:");
}

/** Wait until a task reaches a terminal status — no client-side timeout. */
export function waitForTaskTerminalViaWebSocket(
  taskId: string,
  options: {
    onProgress?: (update: SnapshotTaskStatus) => void;
    fallbackPollIntervalMs?: number;
  } = {},
): Promise<void> {
  const { onProgress, fallbackPollIntervalMs = FALLBACK_POLL_INTERVAL_MS } = options;

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      unsub?.();
      if (pollTimer) clearInterval(pollTimer);
      action();
    };

    const handleStatus = (update: SnapshotTaskStatus) => {
      if (update.task_id !== taskId) return;
      onProgress?.(update);

      if (!TERMINAL_STATUSES.has(update.status)) return;

      if (update.status === "failed") {
        settle(() => reject(new Error(update.error || update.status_message || "Background task failed")));
      } else if (update.status === "cancelled") {
        settle(() => reject(new Error(update.status_message || "Background task was cancelled")));
      } else {
        settle(() => resolve());
      }
    };

    const unsub = taskWebSocket.subscribe([taskId], handleStatus);

    async function poll() {
      if (settled) return;
      try {
        const status = await getTaskStatus(taskId);
        if (!settled) handleStatus(status);
      } catch {
        // Transient — keep waiting
      }
    }

    poll();
    pollTimer = setInterval(() => {
      if (settled) return;
      poll();
    }, fallbackPollIntervalMs);
  });
}

async function fetchResultWithRetry<T>(
  fetchResult: (taskId: string) => Promise<T>,
  taskId: string,
  maxAttempts = 10,
  intervalMs = 300,
): Promise<T> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fetchResult(taskId);
    } catch (err) {
      const notReady = _isResultNotReady(err);
      if (!notReady || attempt >= maxAttempts - 1) {
        throw new Error(_extractErrorDetail(err));
      }
      await sleep(intervalMs + attempt * 100);
    }
  }
  throw new Error("Timed out waiting for calculation results");
}

export async function fetchTaskResultWithRetry<T>(
  fetchResult: (taskId: string) => Promise<T>,
  taskId: string,
  options: {
    onProgress?: (update: SnapshotTaskStatus) => void;
    resultMaxAttempts?: number;
    resultIntervalMs?: number;
  } = {},
): Promise<T> {
  await waitForTaskTerminalViaWebSocket(taskId, { onProgress: options.onProgress });
  return fetchResultWithRetry(
    fetchResult,
    taskId,
    options.resultMaxAttempts,
    options.resultIntervalMs,
  );
}

export async function waitForTaskCompletion(
  taskId: string,
  options: { onProgress?: (update: SnapshotTaskStatus) => void } = {},
): Promise<void> {
  await waitForTaskTerminalViaWebSocket(taskId, options);
}
